import { useState } from 'react';
import { AlertCircle, Play } from 'lucide-react';
import {
  type AvailableOperation,
  type VarValue,
  isCredentialValue,
  isLeftOut,
  useApplyConfigChange,
  useAvailableOperations,
  withoutLeftOut,
} from '@/hooks/useDeviceActions';
import { OperationVariableInputs } from '@/components/OperationVariableInputs';
import { SiteReachDetail, SiteReachLine } from '@/components/SiteReach';
import { vlanRangesValid } from '@/utils/vlanRanges';
import { apiErrorMessage } from '@/utils/apiError';
import { addressVariableProblem, isAddressVariable, isWholeNumberVariable } from '@/utils/netValidation';
import { type ConfigChange } from '@/hooks/useConfigChanges';
import { deviceCount, formatOperationName, formatVariableName, operationTitle } from '@/utils/formatting';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { AppSheet } from '@/components/ui/app-sheet';
import { cn } from '@/lib/utils';

interface DeviceActionsProps {
  deviceId: string;
}

const RESULT_STYLE: Record<string, string> = {
  success: 'border-sev-4 bg-sev-4/10 text-foreground',
  partial_failure: 'border-sev-3 bg-sev-3-soft text-foreground',
  failure: 'border-destructive bg-destructive/5 text-foreground',
  rejected: 'border-muted bg-muted text-foreground',
  timeout: 'border-sev-3 bg-sev-3-soft text-foreground',
};

/**
 * DeviceActions — vendor-profile-provided operations runnable on this
 * device. Embedded in DeviceDetails > Actions tab.
 *
 * Card grid of available operations. Click Run → opens AppSheet with
 * variable form. Submit → shows result inline; Close returns to the
 * action grid.
 */
export default function DeviceActions({ deviceId }: DeviceActionsProps) {
  const { data: operations, isLoading, isError } = useAvailableOperations(deviceId);
  const applyMutation = useApplyConfigChange(deviceId);

  const [activeOperation, setActiveOperation] = useState<AvailableOperation | null>(null);
  const [variables, setVariables] = useState<Record<string, VarValue>>({});
  const [multiVariables, setMultiVariables] = useState<Record<string, string[]>>({});
  const [lastResult, setLastResult] = useState<ConfigChange | null>(null);

  const openModal = (op: AvailableOperation) => {
    setActiveOperation(op);
    setLastResult(null);
    const defaults: Record<string, VarValue> = {};
    const multiDefaults: Record<string, string[]> = {};
    for (const v of op.variables) {
      if (v.multiSelect && v.possibleValues) multiDefaults[v.name] = [];
      else defaults[v.name] = v.varType === 'vlan_range_list' ? [] : (v.default ?? '');
    }
    setVariables(defaults);
    setMultiVariables(multiDefaults);
  };

  const closeModal = () => {
    setActiveOperation(null);
    setVariables({});
    setMultiVariables({});
    setLastResult(null);
  };

  // A multi-select variable (e.g. several interfaces) expands into one
  // operation per selected value, applied in a single config change.
  const buildOperations = () => {
    if (!activeOperation) return [];
    const sent = withoutLeftOut(activeOperation.variables, variables);
    const multiVar = activeOperation.variables.find(
      (v) => v.multiSelect && v.possibleValues && (multiVariables[v.name] ?? []).length > 0,
    );
    if (!multiVar) {
      return [{ operationName: activeOperation.operationName, variables: sent }];
    }
    return (multiVariables[multiVar.name] ?? []).map((val) => ({
      operationName: activeOperation.operationName,
      variables: { ...sent, [multiVar.name]: val },
    }));
  };

  const handleSubmit = () => {
    if (!activeOperation) return;
    applyMutation.mutate(
      { operations: buildOperations() },
      {
        onSuccess: (result) => { setLastResult(result); },
      },
    );
  };

  const isFormValid = () => {
    if (!activeOperation) return false;
    return activeOperation.variables.every((v) => {
      if (isLeftOut(v, variables[v.name])) return true;
      if (v.multiSelect && v.possibleValues) {
        return (multiVariables[v.name] ?? []).length > 0;
      }
      const val = variables[v.name] ?? '';
      if (v.varType === 'credential') return isCredentialValue(val);
      if (v.varType === 'vlan_range_list') {
        return Array.isArray(val) && vlanRangesValid(val);
      }
      if (isAddressVariable(v.varType)) {
        return typeof val === 'string' && addressVariableProblem(v.varType, val, v.family) === null;
      }
      if (typeof val !== 'string' || val.trim() === '') return false;
      if (isWholeNumberVariable(v.varType)) {
        const num = Number(val);
        if (!Number.isInteger(num)) return false;
        if (v.min !== null && num < v.min) return false;
        if (v.max !== null && num > v.max) return false;
      }
      return true;
    });
  };

  if (isLoading) {
    return <Skeleton className="h-48 w-full" />;
  }
  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load available actions.</AlertDescription>
      </Alert>
    );
  }
  if (!operations || operations.length === 0) {
    return (
      <EmptyState
        variant="no-data"
        title="No actions available"
        description="The device may not have a matching vendor profile."
        size="inline"
      />
    );
  }

  return (
    <>
      <section className="bg-card border border-border rounded-lg p-5">
        <h2 className="text-sm font-semibold mb-1">Actions</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Run configuration changes on this device.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {operations.map((op) => (
            <article
              key={op.operationName}
              className="flex flex-col rounded-md border border-border-subtle bg-bg-subtle p-4"
            >
              <h3 className="text-sm font-semibold">{operationTitle(op)}</h3>
              {op.via ? (
                <p className="text-[11px] text-muted-foreground mt-0.5">via {op.via}</p>
              ) : null}
              {op.site ? <SiteReachLine site={op.site} className="mt-0.5" /> : null}
              {op.description ? (
                <p className="text-xs text-muted-foreground mt-1 flex-1">{op.description}</p>
              ) : (
                <div className="flex-1" />
              )}
              {op.variables.length > 0 ? (
                <div className="flex flex-wrap gap-1 mt-3">
                  {op.variables.map((v) => (
                    <Badge key={v.name} variant="secondary" className="text-[10px] font-normal">
                      {formatVariableName(v.name)}
                    </Badge>
                  ))}
                </div>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                onClick={() => { openModal(op); }}
                className="mt-3 self-start"
              >
                <Play className="w-3.5 h-3.5" />
                Run
              </Button>
            </article>
          ))}
        </div>
      </section>

      <AppSheet
        open={activeOperation !== null}
        onOpenChange={(open) => {
          if (!open) closeModal();
        }}
        title={activeOperation ? operationTitle(activeOperation) : ''}
      >
        <AppSheet.Body>
          {activeOperation?.description ? (
            <p className="text-sm text-muted-foreground mb-4">{activeOperation.description}</p>
          ) : null}
          {activeOperation?.via ? (
            <p className="text-sm text-muted-foreground mb-4">
              {activeOperation.via}, the controller that manages this device, makes this change. The
              device itself is not contacted.
            </p>
          ) : null}
          {activeOperation?.site ? <SiteReachDetail site={activeOperation.site} className="mb-4" /> : null}

          {activeOperation ? (
            <OperationVariableInputs
              variables={activeOperation.variables}
              values={variables}
              onValuesChange={setVariables}
              multiValues={multiVariables}
              onMultiChange={setMultiVariables}
              disabled={applyMutation.isPending}
              idPrefix="action-var"
            />
          ) : null}

          {lastResult ? (
            <Alert className={cn('mt-4', RESULT_STYLE[lastResult.status])}>
              <AlertDescription>
                <p className="font-semibold mb-2">Result: {lastResult.status}</p>
                {lastResult.result?.operationResults.map((r, i) => (
                  <div key={i} className="text-sm">
                    <p className="font-medium">
                      {formatOperationName(r.operationName)} — {r.status} via {r.protocolUsed}
                    </p>
                    {r.output ? (
                      <pre className="mt-1 text-xs font-mono whitespace-pre-wrap bg-bg-subtle rounded p-2 overflow-x-auto">
                        {r.output}
                      </pre>
                    ) : null}
                    {r.error ? (
                      <p className="text-destructive text-xs mt-1">{r.error}</p>
                    ) : null}
                  </div>
                ))}
              </AlertDescription>
            </Alert>
          ) : null}

          {applyMutation.isError && !lastResult ? (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {apiErrorMessage(applyMutation.error, 'Failed to apply configuration change. Please try again.')}
              </AlertDescription>
            </Alert>
          ) : null}
        </AppSheet.Body>
        <AppSheet.Footer>
          {lastResult ? (
            <Button onClick={closeModal}>Close</Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={closeModal}
                disabled={applyMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!isFormValid() || applyMutation.isPending}
              >
                {applyMutation.isPending
                  ? 'Applying…'
                  : activeOperation?.site
                    ? `Apply to ${deviceCount(activeOperation.site.devices.length)}`
                    : 'Apply'}
              </Button>
            </>
          )}
        </AppSheet.Footer>
      </AppSheet>
    </>
  );
}
